import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useLocation } from "react-router";
import { isEmpty } from "lodash";

import { useAppState } from "core/contexts/AppContext";
import { getCanRenderWebp } from "core/includes/image";
import { getURLSegment, getURLSegments } from "core/includes/formatters";
import { pageMenuQuery } from "core/includes/queries";

export type Breadcrumb = { label: string, link: string };

/**
 * Derive the breadcrumb chain (Home first) from the page menu tree. This is the
 * single source of truth shared by the visual breadcrumb and the JSON-LD emitter.
 */
export const buildBreadcrumbs = (
    pages: StandardMenu[] | undefined,
    pathname: string,
    currentPageName: string,
): Breadcrumb[] => {
    const urlSegments = getURLSegments(pathname);
    const currentURLSegment = getURLSegment(pathname);

    const getPageBySegment = (selectedSegment: string): StandardPage | undefined => {
        const segmentIndex = urlSegments.indexOf(selectedSegment);
        const segment = segmentIndex === 0 ? 'headerMenu' : urlSegments[segmentIndex - 1];
        const parentPage = pages?.find((page: StandardMenu) => page.segment === segment);
        return parentPage?.standardPage?.find((page: StandardPage) => page.uRLSegment === selectedSegment);
    };

    const getLinkBySegment = (selectedSegment: string): Breadcrumb => {
        const segmentIndex = urlSegments.indexOf(selectedSegment);
        const currentPage = getPageBySegment(selectedSegment);

        let link = '';
        for (let i = 0; i <= segmentIndex; i++) {
            link += '/' + urlSegments[i];
        }

        const label = currentPage?.title
            ?? (currentURLSegment === selectedSegment && isEmpty(currentPage?.title) ? currentPageName : '');

        return { label, link };
    };

    const crumbs: Breadcrumb[] = [{ label: 'Home', link: '/' }];
    urlSegments.forEach((segment: string) => {
        if (segment !== 'home' && segment !== '') {
            const crumb = getLinkBySegment(segment);
            if (!isEmpty(crumb.label)) {
                crumbs.push(crumb);
            }
        }
    });

    return crumbs;
};

/**
 * Build a BreadcrumbList JSON-LD string. Returns null when there is nothing
 * meaningful to emit (single-item chain, e.g. home, or no absolute URL).
 */
export const buildBreadcrumbJsonLd = (breadcrumbs: Breadcrumb[], absoluteUrl: string): string | null => {
    if (breadcrumbs.length < 2 || !absoluteUrl) {
        return null;
    }

    const origin = new URL(absoluteUrl).origin;

    return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.label,
            ...(index < breadcrumbs.length - 1 && { item: origin + item.link }),
        })),
    });
};

const useBreadcrumbs = (): Breadcrumb[] => {
    const location = useLocation();
    const [appState] = useAppState();

    // Reproduce Header.tsx's exact query variables so this reads the same Apollo
    // cache entry instead of triggering a separate fetch. The Selected-Country
    // context header is not part of the cache key, so it is omitted here.
    const stage = new URLSearchParams(location.search).get('stage');
    const canRenderWebp = getCanRenderWebp();
    const { data } = useQuery(pageMenuQuery, { variables: { stage, canRenderWebp } });

    return useMemo(
        () => buildBreadcrumbs(data?.pageMenu, location.pathname, appState?.currentPageName ?? ''),
        [data?.pageMenu, location.pathname, appState?.currentPageName],
    );
};

export default useBreadcrumbs;
